import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Avatar,
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import { TbExternalLink } from "react-icons/tb";
import { useSelector, useDispatch } from "react-redux";
import { TeamTableAction } from "../../../Redux/action/TeamTableAction";
import { Link, useLocation } from "react-router-dom";
import Pagination from "../../Common/Pagination/Pagination";
import { ToastContainer } from "react-toastify";

const TeamTable = ({
  selectedCandidates,
  setSelectedCandidates,
  search,
  openModal,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const vId = queryParams.get("vendorId");

  const dispatch = useDispatch();
  const { userResult } = useSelector((state) => state.Auth);
  const vendorid = vId || userResult?.vendorid;

  const userInfo = useSelector((state) => state?.TeamTable?.result?.content);
  const totalPages = useSelector((state) => state?.TeamTable?.result?.totalPages);

  const [pageNumber, setPageNumber] = useState(0);

  useEffect(() => {
    dispatch(
      TeamTableAction(vendorid, "", search, pageNumber, location.pathname)
    );
  }, [pageNumber, search]);

  const handleCandidateSelection = (e, candidateId) => {
    setSelectedCandidates((prev) =>
      e.target.checked
        ? [...prev, candidateId]
        : prev.filter((id) => id !== candidateId)
    );
  };

  // 🔹 Better badge object w/ color + soft background
  const getStatusBadge = (status) => {
    const config = {
      COMPLETE: {
        label: "Complete",
        color: "#16a34a",
        bg: "rgba(22,163,74,0.15)",
      },
      PARTIALLY_COMPLETE: {
        label: "Partial",
        color: "#ca8a04",
        bg: "rgba(202,138,4,0.15)",
      },
      INCOMPLETE: {
        label: "Incomplete",
        color: "#dc2626",
        bg: "rgba(220,38,38,0.15)",
      },
    };

    return config[status] || config.INCOMPLETE;
  };

  return (
    <Box sx={{ mt: 3 }} className="common_wrapper">
      <ToastContainer />

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          border: "1px solid #E5E8EB",
          background: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "20px", color: "#0E3F6F" }}
        >
          Team Members
        </Typography>

        {selectedCandidates.length > 0 && (
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 500, borderRadius: 2 }}
            onClick={openModal}
          >
            Close Account ({selectedCandidates.length})
          </Button>
        )}
      </Paper>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid #E5E7EB",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: "#F9FAFB" , textTransform: "uppercase"}}>
              {!location.pathname.includes("/admin") && (
                <TableCell sx={{ fontWeight: 600 }}>Select</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>Candidate</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Profile Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {userInfo?.map((item, i) => {
              const badge = getStatusBadge(item.profileCompleteness);

              return (
                <TableRow
                  key={i}
                  sx={{
                    transition: "0.2s",
                    "&:hover": { background: "#F5FAFF" },
                  }}
                >
                  {!location.pathname.includes("/admin") && (
                    <TableCell>
                      <Checkbox
                        onChange={(e) =>
                          handleCandidateSelection(e, item.candidateId)
                        }
                      />
                    </TableCell>
                  )}

                  {/* Candidate Cell */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
                      <Avatar
                        src={item.profileImageUrl}
                        sx={{
                          width: 45,
                          height: 45,
                          fontWeight: 600,
                          bgcolor: "#0E3F6F",
                          fontSize: "15px",
                        }}
                      >
                        {(item.firstName?.[0] || "") +
                          (item.lastName?.[0] || "")}
                      </Avatar>

                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
                          {item.firstName} {item.lastName}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#6b7280", fontWeight: 600 }}>
                          ID: {item.candidateEmpId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>{item.currentDesignation}</TableCell>
                  <TableCell>{item.department.replace("_", " ")}</TableCell>
                  <TableCell>{item.email}</TableCell>

                  {/* Status Badge */}
                  <TableCell>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.6,
                        borderRadius: "12px",
                        fontSize: "13px",
                        fontWeight: 500,
                        width: "fit-content",
                        color: badge.color,
                        background: badge.bg,
                      }}
                    >
                      {badge.label}
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="View Profile">
                        <IconButton
                          component={Link}
                          to={
                            location.pathname.includes("/admin")
                              ? `/admin/view/team-member?vendorId=${vId}&candidateId=${item?.candidateId}`
                              : `/view_candidate/${item.candidateId}`
                          }
                        >
                          <TbExternalLink size={20} color="#0E3F6F" />
                        </IconButton>
                      </Tooltip>

                      {!location.pathname.includes("/admin") && (
                        <Tooltip title="Edit">
                          <IconButton
                            component={Link}
                            to={`/update-team/${item.candidateId}`}
                          >
                            <CiEdit size={20} color="#0E3F6F" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {!userInfo?.length && (
        <Typography sx={{ textAlign: "center", py: 3, color: "#6b7280" }}>
          No Team Members Found
        </Typography>
      )}

      {/* Pagination */}
      {userInfo?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            totalPages={totalPages}
            currentPage={pageNumber}
            onPageChange={setPageNumber}
          />
        </Box>
      )}
    </Box>
  );
};

export default TeamTable;
